import React, { useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import { getText, LanguageService } from 'services/languageService';
import { resetDiagramDataAction } from 'store/actions/diagrams';
import { setDefaultLayerAction } from 'store/actions/layers';
import {
  getStatLayersSelectAction,
  setCurrentTabAction,
} from 'store/actions/mainPage';
import { resetPieDataAction } from 'store/actions/pieData';
import { resetTableDataAction } from 'store/actions/tables';
import { MapToolbarProps } from 'types/mainPage';
import { a11yProps, TabPanel } from 'components/UI/TabPanel';
import { MapDrawer } from 'components/UI/MapDrawer';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { Box, IconButton, Tab, Tabs, Tooltip } from '@mui/material';
import { tabsData } from './constants';

export const MapToolbar: React.FC<MapToolbarProps> = ({ open, toggleOpen }) => {
  const [tabValue, setTabValue] = useState<number>(0);
  const dispatch = useAppDispatch();
  const { isLogged, currentDataset } = useAppSelector(
    (state) => state.mainPage
  );
  const { currentLayerId, currentYear, foundInfo } = useAppSelector(
    (state) => state.layers
  );
  const lang = LanguageService.getLanguage();

  const openToolbar = () => {
    toggleOpen(true);
  };

  const closeToolbar = () => {
    toggleOpen(false);
  };

  const handleChangeTab = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    openToolbar();
    dispatch(setCurrentTabAction(tabsData[newValue].label));
  };

  useEffect(() => {
    const currentPath = window.location.href;
    const layerParam = '?tree=';

    if (currentPath.includes(layerParam) && !currentLayerId) {
      const parts = currentPath.split(layerParam);
      const id = parts[parts.length - 1];
      dispatch(setDefaultLayerAction(+id));
    }
  }, []);

  useEffect(() => {
    if (!currentLayerId) {
      dispatch(resetTableDataAction());
      dispatch(resetPieDataAction());
      dispatch(resetDiagramDataAction());
    }
  }, [currentLayerId]);

  //get layers after change dataset
  useEffect(() => {
    if (currentDataset) {
      dispatch(
        getStatLayersSelectAction(
          isLogged,
          currentDataset.id,
          currentYear,
          !!foundInfo
        )
      );
    }
  }, [currentDataset]);

  const data = useMemo(() => {
    const tooltips = {
      layers: getText('LAYERS_TL'),
      table: getText('TABLES_TL'),
      pie: getText('PIE_TAB_TL'),
      diagrams: getText('DIAGRAMS_TL'),
      search: getText('SEARCH_TL'),
    };

    return tabsData.map((item) => {
      return {
        ...item,
        name: tooltips[item.label],
        id: tooltips[item.label],
      };
    });
  }, [lang]);

  return (
    <MapDrawer open={open} variant="permanent">
      <Box sx={{ display: 'flex', flex: 1 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            width: '50px',
            borderRight: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Tabs
            value={tabValue}
            onChange={handleChangeTab}
            aria-label="icon tabs example"
            orientation="vertical"
            onClick={() => {
              if (!open) {
                openToolbar();
              }
            }}
          >
            {data.map((item) => {
              return (
                <Tooltip
                  key={item.id}
                  title={item.name}
                  placement="right"
                  componentsProps={{
                    popper: {
                      sx: {
                        div: {
                          backgroundColor: (theme) =>
                            theme.palette.custom.tooltipBg,
                          color: 'black',
                          fontSize: '12px',
                        },
                      },
                    },
                  }}
                >
                  <Tab
                    sx={{ minWidth: '50px', p: 0 }}
                    icon={item.tabIcon}
                    aria-label={item.label}
                    key={item.label}
                    {...a11yProps(item.value)}
                  />
                </Tooltip>
              );
            })}
          </Tabs>
        </Box>

        <Box
          sx={{
            flex: 1,
            background: 'white',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              height: '30px',
              display: 'flex',
              justifyContent: 'flex-end',
              backgroundColor: (theme) => theme.palette.grey[300],
            }}
          >
            <IconButton onClick={closeToolbar} color={'inherit'}>
              <ChevronLeftIcon />
            </IconButton>
          </Box>
          {tabsData.map((item) => {
            return (
              <TabPanel
                value={tabValue}
                index={item.value}
                key={`tab-${item.label}`}
                sx={{ flex: 1 }}
              >
                {item.tabComponent}
              </TabPanel>
            );
          })}
        </Box>
      </Box>
    </MapDrawer>
  );
};
